import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiResponse, PageableDto } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import { mapMutationMessage } from '../../utils/map-pageable';

/** Shape API de un bloqueo de cumplimiento del customer. */
export interface CustomerComplianceBlockDto {
  id: string;
  customerId?: string;
  registeredAt?: string;
  folio?: string;
  code?: string;
  typeBlockId?: string;
  typeBlockName?: string;
  typeBlock?: { id?: string; name?: string; text?: string };
  startsAt?: string;
  endsAt?: string | null;
  reasonId?: string | null;
  reasonName?: string | null;
  reason?: { id?: string; name?: string; text?: string };
  customReason?: string | null;
  observations?: string | null;
  userName?: string | null;
  createdByName?: string | null;
  createdBy?: { name?: string; fullName?: string };
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Body de POST/PUT compliance-blocks.
 * Con motivo de catálogo se envía `reasonId`; con motivo libre, `customReason`.
 */
export type CustomerComplianceBlockWritePayload = {
  registeredAt: string;
  typeBlockId: string;
  startsAt: string;
  endsAt: string;
  reasonId?: string;
  customReason?: string;
  observations?: string;
};

export type CustomerComplianceBlockListParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  status?: string;
  search?: string;
};

export type CustomerComplianceBlockStatus =
  | 'ACTIVE'
  | 'CANCELLED'
  | 'REVIEW';

/**
 * Bloqueos de cumplimiento del cliente.
 *   GET    /api/customers/{customerId}/compliance-blocks
 *   POST   /api/customers/{customerId}/compliance-blocks
 *   PUT    /api/customers/{customerId}/compliance-blocks/{id}
 *   PATCH  /api/customers/{customerId}/compliance-blocks/{id}/status
 *   DELETE /api/customers/{customerId}/compliance-blocks/{id}
 */
@Injectable({ providedIn: 'root' })
export class CustomerComplianceBlockService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private resource(customerId: string): string {
    return `${this.apiConfig.apiUrl}/customers/${customerId}/compliance-blocks`;
  }

  list(
    customerId: string,
    params: CustomerComplianceBlockListParams = {},
  ): Observable<CustomerComplianceBlockDto[]> {
    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 100))
      .set('sort', params.sort ?? 'createdAt')
      .set('direction', params.direction ?? 'desc');

    if (params.status?.trim()) {
      httpParams = httpParams.set('status', params.status.trim());
    }
    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    return this.http
      .get<
        ApiResponse<
          | CustomerComplianceBlockDto[]
          | PageableDto<CustomerComplianceBlockDto>
        >
      >(this.resource(customerId), { params: httpParams })
      .pipe(map((res) => this.unwrapList(res.data)));
  }

  create(
    customerId: string,
    payload: CustomerComplianceBlockWritePayload,
  ): Observable<CustomerComplianceBlockDto> {
    return this.http
      .post<ApiResponse<CustomerComplianceBlockDto>>(
        this.resource(customerId),
        payload,
      )
      .pipe(map((res) => res.data as CustomerComplianceBlockDto));
  }

  update(
    customerId: string,
    blockId: string,
    payload: CustomerComplianceBlockWritePayload,
  ): Observable<CustomerComplianceBlockDto> {
    return this.http
      .put<ApiResponse<CustomerComplianceBlockDto>>(
        `${this.resource(customerId)}/${blockId}`,
        payload,
      )
      .pipe(map((res) => res.data as CustomerComplianceBlockDto));
  }

  updateStatus(
    customerId: string,
    blockId: string,
    status: CustomerComplianceBlockStatus,
  ): Observable<{ message: string }> {
    return this.http
      .patch<{ success?: boolean; message?: string }>(
        `${this.resource(customerId)}/${blockId}/status`,
        { status },
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Estatus actualizado exitosamente'),
        ),
      );
  }

  delete(
    customerId: string,
    blockId: string,
  ): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(
        `${this.resource(customerId)}/${blockId}`,
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Bloqueo eliminado exitosamente'),
        ),
      );
  }

  private unwrapList(
    data:
      | CustomerComplianceBlockDto[]
      | PageableDto<CustomerComplianceBlockDto>
      | null
      | undefined,
  ): CustomerComplianceBlockDto[] {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.content)) return data.content;
    return [];
  }
}
